import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import LoaderOverlay from "@/components/ui/loader-overlay";

import {
  useSuspenseQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from "@tanstack/react-query";
import { format } from "date-fns";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import z from "zod";
import { listBusinessOffers } from "@/queries/api/businessShiftApi";
import { listWorkerOffers } from "@/queries/api/workerShiftApi";
import type { Offer } from "@/schemas/offer";

import { ChatPanel } from "./-components/ChatPanel";
import { ConversationList } from "./-components/ConversationList";
import { OfferDetails } from "./-components/OfferDetails";
import { OffersList } from "./-components/OffersList";
import type { ConversationSummary } from "@/schemas/message";

import {
  fetchConversationMessages,
  sendConversationMessage,
  fetchConversationSummary,
} from "@/queries/api/messageApi";
import {
  getEmployerProfileForWorker,
  getWorkerProfileForEmployer,
} from "@/queries/api/profileApi";
import { QueryKeys } from "@/queries/queryKeys";

import { useRole } from "@/utils/hooks/use-role";
import type { Role } from "@/utils/hooks/use-role";
import { notify } from "@/utils";
import { useAuth } from "@/context/auth";
import BackButton from "@/components/back-button";
import { useIsMobile } from "@/utils/hooks/use-is-mobile";
import { useTranslation } from "react-i18next";

function makeConversationQuery(role: Role) {
  return {
    queryKey: [QueryKeys.MESSAGES, "conversations", role],
    queryFn: async (): Promise<Array<ConversationSummary>> => {
      const res = await fetchConversationSummary({
        conversation_type: "CONVERSATION",
      });
      return res.conversation;
    },
  };
}

function makeProfileQuery(role: Role, to?: string, businessId?: number) {
  return {
    queryKey: ["profile", role, businessId || to],
    queryFn: async () => {
      if (role === "worker" && businessId) {
        return getEmployerProfileForWorker(businessId);
      } else if (role === "employer" && to) {
        return getWorkerProfileForEmployer(to);
      }
      return null;
    },
  };
}

export const Route = createFileRoute("/app/$role/_app/messages/")({
  validateSearch: z.object({
    tab: z.string().optional(),
    to: z.string().optional(),
    businessId: z.number().optional(),
    selectedOfferId: z.number().optional(),
  }),
  loader: ({ params, context }) => {
    const role = params.role as Role;
    context.queryClient.ensureQueryData({
      queryKey: [QueryKeys.MESSAGES, "offers", role],
      queryFn: async () => {
        if (role === "worker") {
          const res = await listWorkerOffers();
          return res.offers;
        } else {
          const res = await listBusinessOffers();
          return res.offers;
        }
      },
    });
    context.queryClient.ensureQueryData(makeConversationQuery(role));
  },
  component: RouteComponent,
});

const getTabs = (role: Role, t: (k: string) => string) => [
  { value: "messages", label: t("tabs.messages") },
  role === "employer"
    ? { value: "offers", label: t("tabs.offersSent") }
    : { value: "offers", label: t("tabs.offersReceived") },
  role === "employer"
    ? { value: "interest", label: t("tabs.interestReceived") }
    : { value: "interest", label: t("tabs.interestSent") },
];

function RouteComponent() {
  const { t } = useTranslation("messages");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Mock data for conversations
  const params = Route.useSearch();
  const [activeTab, setActiveTab] = useState<string>(
    params.tab === "all" ? "messages" : (params.tab ?? "messages"),
  );
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [hasInitializedFromParams, setHasInitializedFromParams] =
    useState(false);

  const role = useRole();
  const search = Route.useSearch();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: conversations } = useSuspenseQuery(makeConversationQuery(role));

  // Fetch all offers for the current role
  const { data: offersData = { offers: [], users: {} } } = useSuspenseQuery({
    queryKey: ["messages-offers", role],
    queryFn: async () => {
      if (role === "worker") {
        const res = await listWorkerOffers();
        return { offers: res.offers, users: res.users || {} };
      } else {
        const res = await listBusinessOffers();
        return { offers: res.offers, users: res.users || {} };
      }
    },
  });
  const allOffers = offersData.offers;
  const offerUsers = offersData.users;

  // Update selectedOffer when offers refetch after mutation
  React.useEffect(() => {
    if (selectedOffer && allOffers.length > 0) {
      const updated = allOffers.find((o) => o.id === selectedOffer.id);
      if (updated) setSelectedOffer(updated);
    }
  }, [allOffers, selectedOffer]);

  // Filter offers for tabs
  const offersByBusiness = allOffers.filter(
    (offer) => offer.offeredBy === "business",
  );
  const offersByWorker = allOffers.filter(
    (offer) => offer.offeredBy === "worker",
  );

  // Open offer detail if selectedOfferId is present in params
  useEffect(() => {
    if (!hasInitializedFromParams && params.selectedOfferId) {
      const offerId = Number(params.selectedOfferId);
      const offer = [...offersByBusiness, ...offersByWorker].find(
        (o) => o.id === offerId,
      );
      if (offer) {
        setActiveTab(params.tab || "offers");
        setSelectedOffer(offer);
        setHasInitializedFromParams(true);
      } else {
        // If offerId is invalid, clear selection and update URL
        setSelectedOffer(null);
        setHasInitializedFromParams(true);
        navigate({
          search: {
            ...search,
            selectedOfferId: undefined,
          } as any,
          replace: true,
        });
      }
    }
  }, [
    params.selectedOfferId,
    offersByBusiness,
    offersByWorker,
    params.tab,
    hasInitializedFromParams,
    navigate,
    params,
  ]);

  // Fetch profile for new conversation
  const {
    data: newConversationProfile,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
  } = useQuery(makeProfileQuery(role, params.to, params.businessId));

  const sendMessageMutation = useMutation({
    mutationFn: async (args: {
      to: string;
      message: string;
      conversation_type?: "CONVERSATION" | "OFFER" | "PAYMENT";
    }) => {
      return await sendConversationMessage(args);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MESSAGES, "conversations", role],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MESSAGES, "messages", selectedConversationId],
      });
    },
  });

  // Handle navigation from employer profile
  useEffect(() => {
    if (params.to) {
      const existing = conversations.find((c) => c.user_id === params.to);
      if (existing) {
        setSelectedConversationId(params.to);
        setActiveTab("messages");
      } else {
        setSelectedConversationId("new:" + params.to);
        setActiveTab("messages");
      }
    }
  }, [params.to, conversations]);

  // Sync tab
  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setSelectedConversationId(null);
      setSelectedOffer(null);
      setHasInitializedFromParams(true);
      navigate({
        search: {
          ...search,
          tab,
          to: undefined,
          businessId: undefined,
          selectedOfferId: undefined,
        } as any,
        replace: false,
      });
    },
    [navigate, params],
  );

  // When an offer is selected, update state
  const handleSelectOffer = useCallback(
    (offer: Offer) => {
      setSelectedOffer(offer);
      setHasInitializedFromParams(true);
      // On mobile, set selectedConversationId to a special offer value
      if (isMobile) {
        setSelectedConversationId(`offer:${offer.id}`);
      }
      navigate({
        search: {
          ...search,
          tab: activeTab,
          to: undefined,
          businessId: undefined,
          selectedOfferId: offer.id,
        } as any,
        replace: false,
      });
    },
    [navigate, params, activeTab, isMobile],
  );

  const selectedSummary = conversations.find(
    (c) => c.user_id === selectedConversationId,
  );

  const {
    data: selectedMessages = [],
    isLoading: isMessagesLoading,
    isFetching: isMessagesFetching,
  } = useQuery({
    queryKey: [QueryKeys.MESSAGES, "messages", selectedConversationId],
    queryFn: async () => {
      if (!selectedConversationId || selectedConversationId.startsWith("new:"))
        return [];
      const userId = selectedConversationId;
      const res = await fetchConversationMessages({
        with: userId,
        conversation_type: "CONVERSATION",
      });
      return res.conversation.map((conv) => ({
        id: conv.id.toString(),
        sender:
          conv.fromUser === user?.userId
            ? "me"
            : role === "worker"
              ? "employer"
              : "worker",
        text: conv.message,
        time: conv.createdAt,
      }));
    },
    enabled:
      !!selectedConversationId && !selectedConversationId.startsWith("new:"),
  });

  const isNewConversation = selectedConversationId?.startsWith("new:");
  const newEmployerId = isNewConversation
    ? selectedConversationId!.split(":")[1]
    : null;

  if (isMobile) {
    // Mobile stack view logic
    // Show details panel if selectedConversationId is set (chat) OR if it's an offer ("offer:<id>")
    const isOfferPanel =
      selectedConversationId && selectedConversationId.startsWith("offer:");
    if (!selectedConversationId || (isOfferPanel && !selectedOffer)) {
      // Show tabs and lists
      return (
        <React.Suspense fallback={<LoaderOverlay />}>
          <div className="h-full w-full flex flex-col bg-white">
            <div className="px-4 pt-4 pb-2">
              <div className="text-lg font-bold text-garnet">{t("title")}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {format(new Date(), "EEE, MMM dd, yyyy")}
              </div>
            </div>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="flex-1"
            >
              <TabsList className="rounded-none w-full pt-4 pb-0 bg-transparent">
                {getTabs(role, t).map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={"body-small text-slate!"}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="messages" className="p-0">
                <ConversationList
                  conversations={conversations}
                  selectedId={selectedConversationId}
                  onSelect={setSelectedConversationId}
                />
              </TabsContent>
              <TabsContent value="offers" className="p-0">
                <OffersList
                  selectedOffer={selectedOffer}
                  onSelectOffer={handleSelectOffer}
                  offers={
                    role === "worker" ? offersByBusiness : offersByBusiness
                  }
                  users={offerUsers}
                />
              </TabsContent>
              <TabsContent value="interest" className="p-0">
                <OffersList
                  selectedOffer={selectedOffer}
                  onSelectOffer={handleSelectOffer}
                  offers={role === "worker" ? offersByWorker : offersByWorker}
                  users={offerUsers}
                />
              </TabsContent>
            </Tabs>
          </div>
        </React.Suspense>
      );
    }
    // Show chat panel or offer details
    return (
      <React.Suspense fallback={<LoaderOverlay />}>
        <div className="h-full w-full flex flex-col bg-white">
          <div className="p-4">
            <BackButton
              onClick={() => {
                setSelectedConversationId(null);
                setSelectedOffer(null);
              }}
            />
          </div>
          <div className="flex-1 flex flex-col">
            <RightPanelContent
              activeTab={activeTab}
              selectedOffer={selectedOffer}
              offerUsers={offerUsers}
              isNewConversation={isNewConversation}
              newConversationProfile={newConversationProfile}
              newEmployerId={newEmployerId}
              sendMessageMutation={sendMessageMutation}
              notify={notify}
              selectedMessages={selectedMessages}
              selectedSummary={selectedSummary}
              selectedConversationId={selectedConversationId}
              role={role}
              setSelectedConversationId={setSelectedConversationId}
              navigate={navigate}
            />
          </div>
        </div>
      </React.Suspense>
    );
  }

  // Desktop: keep two-column layout
  return (
    <>
      <BackButton />
      <div className="flex h-full w-full gap-6 p-6">
        {/* Sidebar with its own Suspense boundary */}
        <React.Suspense
          fallback={
            <div className="w-[340px] flex items-center justify-center">
              <LoaderOverlay />
            </div>
          }
        >
          <div className="w-[340px] flex flex-col bg-white p-0 rounded-2xl shadow">
            <div className="px-6 pt-6 pb-2">
              <div className="text-lg font-bold text-garnet">{t("title")}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {format(new Date(), "EEE, MMM dd, yyyy")}
              </div>
            </div>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="flex-1"
            >
              <TabsList className="rounded-none w-full pt-4 pb-0 bg-transparent">
                {getTabs(role, t).map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={"body-small text-slate!"}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="messages" className="p-0">
                <ConversationList
                  conversations={conversations}
                  selectedId={selectedConversationId}
                  onSelect={setSelectedConversationId}
                />
              </TabsContent>
              <TabsContent value="offers" className="p-0">
                <OffersList
                  selectedOffer={selectedOffer}
                  onSelectOffer={handleSelectOffer}
                  offers={
                    role === "worker" ? offersByBusiness : offersByBusiness
                  }
                  users={offerUsers}
                />
              </TabsContent>
              <TabsContent value="interest" className="p-0">
                <OffersList
                  selectedOffer={selectedOffer}
                  onSelectOffer={handleSelectOffer}
                  offers={role === "worker" ? offersByWorker : offersByWorker}
                  users={offerUsers}
                />
              </TabsContent>
            </Tabs>
          </div>
        </React.Suspense>
        <div className="flex-1 bg-white rounded-xl shadow p-8 flex flex-col relative">
          {/* Loader overlay for right panel */}
          {(isProfileLoading ||
            isProfileFetching ||
            isMessagesLoading ||
            isMessagesFetching) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
              <LoaderOverlay />
            </div>
          )}
          <RightPanelContent
            activeTab={activeTab}
            selectedOffer={selectedOffer}
            offerUsers={offerUsers}
            isNewConversation={isNewConversation}
            newConversationProfile={newConversationProfile}
            newEmployerId={newEmployerId}
            sendMessageMutation={sendMessageMutation}
            notify={notify}
            selectedMessages={selectedMessages}
            selectedSummary={selectedSummary}
            selectedConversationId={selectedConversationId}
            role={role}
            setSelectedConversationId={setSelectedConversationId}
            navigate={navigate}
          />
        </div>
      </div>
    </>
  );
}

// --- Helper components for right panel ---

function RightPanelContent(props: any) {
  const { t } = useTranslation("messages");
  const {
    activeTab,
    selectedOffer,
    offerUsers,
    isNewConversation,
    newConversationProfile,
    newEmployerId,
    sendMessageMutation,
    notify,
    selectedMessages,
    selectedSummary,
    selectedConversationId,
    role,
    navigate,
  } = props;
  const handleProfileClick = () => {
    if (isNewConversation) {
      // For new conversations
      if (role === "worker") {
        // Worker viewing employer/business profile
        const businessId = newConversationProfile?.business?.id;
        if (businessId) {
          navigate({
            to: "/app/profile/employer/$employerId",
            params: { employerId: businessId.toString() },
          });
        }
      } else {
        // Employer viewing worker profile
        const workerId = newConversationProfile?.profile?.id;
        if (workerId) {
          navigate({
            to: "/app/profile/worker/$workerId",
            params: { workerId },
          });
        }
      }
    } else {
      // For existing conversations
      if (role === "worker") {
        // Worker viewing employer/business profile
        const businessId = selectedSummary?.business_id;
        if (businessId) {
          navigate({
            to: "/app/profile/employer/$employerId",
            params: { employerId: businessId.toString() },
          });
        }
      } else {
        // Employer viewing worker profile
        const workerId = selectedSummary?.user_id;
        if (workerId) {
          navigate({
            to: "/app/profile/worker/$workerId",
            params: { workerId },
          });
        }
      }
    }
  };

  if (activeTab === "offers" || activeTab === "interest") {
    return selectedOffer ? (
      <OfferDetails offer={selectedOffer} users={offerUsers} />
    ) : (
      <div className="text-muted-foreground text-center my-auto">
        {t("selectOfferDetails")}
      </div>
    );
  }
  if (isNewConversation) {
    return (
      <ChatPanel
        conversationName={
          role === "worker"
            ? newConversationProfile?.business?.name || t("newMessageFallback")
            : newConversationProfile?.profile?.full_name ||
              t("newMessageFallback")
        }
        messages={[]}
        image={
          role === "worker"
            ? newConversationProfile?.business?.image_url || undefined
            : newConversationProfile?.profile?.avatar_url || undefined
        }
        onSend={async (message) => {
          if (newEmployerId) {
            try {
              const result = await sendMessageMutation.mutateAsync({
                to: newEmployerId,
                message,
                conversation_type: "CONVERSATION" as const,
              });
              if (result.success) {
                notify.success(t("sentSuccess"));
              } else {
                notify.error(result.message || t("sendFailed"));
              }
            } catch {
              notify.error(t("sendFailed"));
            }
          }
        }}
        onProfileClick={handleProfileClick}
      />
    );
  }
  if (selectedMessages.length > 0) {
    return (
      <ChatPanel
        conversationName={
          selectedSummary?.full_name ||
          selectedSummary?.business_name ||
          t("unknown")
        }
        messages={selectedMessages}
        image={
          selectedSummary?.avatar ||
          selectedSummary?.business_image ||
          undefined
        }
        onSend={async (message) => {
          if (selectedConversationId) {
            try {
              const result = await sendMessageMutation.mutateAsync({
                to: selectedConversationId,
                message,
                conversation_type: "CONVERSATION" as const,
              });
              if (result.success) {
                notify.success(t("sentSuccess"));
              } else {
                notify.error(result.message || t("sendFailed"));
              }
            } catch {
              notify.error(t("sendFailed"));
            }
          }
        }}
        onProfileClick={handleProfileClick}
      />
    );
  }
  return (
    <div className="text-muted-foreground text-center my-auto">
      {t("selectConversationDetails")}
    </div>
  );
}
